/**
 * WGS84 GPS 좌표 ↔ 사이트 원점 기준 로컬 미터 좌표 변환.
 *
 * 가정:
 *   - 사이트 원점(`originLon`, `originLat`)이 모델의 (0,0,0)에 매칭됨
 *   - 모델은 1 unit = 1 meter로 익스포트됨
 *   - 사이트 반경 수 km 이내 — 평면 근사 사용 (Mercator 왜곡 무시)
 *
 * Three.js 축 컨벤션:
 *   - +X = 동쪽 (east)
 *   - +Y = 위쪽 (altitude)
 *   - +Z = 남쪽 (south)  ← 위도 증가(북)는 -Z
 */

const METERS_PER_DEG_LAT = 111_320;

export interface LocalCoord {
  x: number;
  z: number;
}

export interface GpsOrigin {
  lon: number;
  lat: number;
}

export function gpsToLocal(
  origin: GpsOrigin,
  targetLon: number,
  targetLat: number
): LocalCoord {
  const latRad = (origin.lat * Math.PI) / 180;
  const x = (targetLon - origin.lon) * METERS_PER_DEG_LAT * Math.cos(latRad);
  const z = -(targetLat - origin.lat) * METERS_PER_DEG_LAT;
  return { x, z };
}

export function distanceMeters(a: LocalCoord, b: LocalCoord = { x: 0, z: 0 }): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.z - b.z) ** 2);
}

/** local 좌표(x=동/+, z=남/+) → WGS84 GPS (gpsToLocal의 역) */
export function localToGps(
  origin: GpsOrigin,
  localX: number,
  localZ: number
): { lon: number; lat: number } {
  const latRad = (origin.lat * Math.PI) / 180;
  const lon = origin.lon + localX / (METERS_PER_DEG_LAT * Math.cos(latRad));
  const lat = origin.lat - localZ / METERS_PER_DEG_LAT;
  return { lon, lat };
}

/** WKT `POINT(lng lat)` → `{ lon, lat }` 파싱. 실패 시 null. */
export function parseWktPoint(wkt?: string | null): GpsOrigin | null {
  if (!wkt) return null;
  const m = wkt.match(/^\s*POINT\s*\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)\s*$/i);
  if (!m) return null;
  const lon = Number(m[1]);
  const lat = Number(m[2]);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  return { lon, lat };
}
